import React from 'react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { extractStatus } from './extractStatus'
import { RecordStatus } from '../../../../models'
import {
    uploadInvoiceForExtraction,
    runInvoiceExtraction,
    getInvoiceExtractionJobStatus,
    getInvoiceExtractionJobResult,
    updateInvoiceExtractionJobResult,
    dispatchInvoiceExtractionProcessing,
    getInvoiceExtractionJobDrafts,
    getActiveInvoiceExtractionJob,
} from '../../../../api/requests'
import { InvoiceExtractionStatus } from './InvoiceExtractionStatus'
import { getAuthUser } from '../../../../helpers'
import defineAbilityFor from './../../defineAbility'

export interface UseInvoiceExtractionPollResult {
    file: File | null
    setFile: (f: File | null) => void
    fileKey: number
    isUploading: boolean
    isPolling: boolean
    jobId: string | null
    status: InvoiceExtractionStatus | null
    handleSubmit: (e: React.FormEvent) => Promise<void>
    checkActiveJob: () => Promise<void>
}

/**
 * invoiceId may be null while invoice loading; pass props.invoice.id
 * onDrafts receives the drafts -> caller should dispatchRecords({type:'bulk-edit', updates})
 */
export function useInvoiceExtractionPoll(
    invoiceId: number | null,
    onDrafts: (updates: Array<any>) => void
): UseInvoiceExtractionPollResult {
    const [file, setFile] = useState<File | null>(null)
    const [fileKey, setFileKey] = useState<number>(Date.now())
    const [isUploading, setIsUploading] = useState(false)
    const [isPolling, setIsPolling] = useState(false)
    const [jobId, setJobId] = useState<string | null>(null)
    const [status, setStatus] = useState<InvoiceExtractionStatus | null>(null)

    const MAX_POLL_RETRIES = 5
    const POLL_INTERVAL = 6000
    const retryRef = useRef<number>(0)
    const pollTimeoutRef = useRef<number | null>(null)
    const hasProcessedRef = useRef<boolean>(false)
    const ability = defineAbilityFor(getAuthUser())

    const clearPollTimeout = useCallback(() => {
        if (pollTimeoutRef.current !== null) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }
    }, [])

    const stopPolling = useCallback(
        (resetJob = true) => {
            clearPollTimeout()
            retryRef.current = 0
            setIsPolling(false)

            if (resetJob) {
                setJobId(null)
                setStatus(null)
                hasProcessedRef.current = false
            }
        },
        [clearPollTimeout]
    )

    useEffect(() => {
        return () => {
            clearPollTimeout()
        }
    }, [clearPollTimeout])

    const pollStatus = async (jobIdArg: string) => {
        try {
            const response = await getInvoiceExtractionJobStatus(jobIdArg)
            const jobStatus: InvoiceExtractionStatus = response.data.status

            retryRef.current = 0
            setStatus(jobStatus)

            if (jobStatus === InvoiceExtractionStatus.SUCCESS) {
                clearPollTimeout()
                
                if (hasProcessedRef.current) return
                hasProcessedRef.current = true
                
                const resultResponse = await getInvoiceExtractionJobResult(jobIdArg)
                const extractionData = resultResponse.data
                
                await updateInvoiceExtractionJobResult(jobIdArg, {
                    data: extractionData,
                    status: InvoiceExtractionStatus.SUCCESS,
                })
                
                await dispatchInvoiceExtractionProcessing(jobIdArg)
                
                setIsPolling(true)
                pollDrafts(jobIdArg)
                
                toast.success('Extraction completed successfully')
                return
            }

            if (jobStatus === InvoiceExtractionStatus.FAILED) {
                stopPolling(true)
                const errorMessage = response.data.error || response.data.message || 'Extraction failed.'
                toast.error(errorMessage)
                return
            }

            clearPollTimeout()
            pollTimeoutRef.current = window.setTimeout(
                () => pollStatus(jobIdArg),
                POLL_INTERVAL
            )
        } catch (err: any) {
            retryRef.current += 1

            if (retryRef.current >= MAX_POLL_RETRIES) {
                stopPolling(true)
                toast.error('Connection issue. Please try again.')
                return
            }

            clearPollTimeout()
            pollTimeoutRef.current = window.setTimeout(
                () => pollStatus(jobIdArg),
                POLL_INTERVAL
            )
        }
    }

    const pollDrafts = async (jobIdArg: string) => {
        try {
            const response = await getInvoiceExtractionJobDrafts(jobIdArg)
            const data = response.data

            if (data.status === InvoiceExtractionStatus.FAILED) {
                stopPolling(true)

                const errorMessage = data.error || data.message || 'Draft processing failed.'

                toast.error(errorMessage)
                return
            }

            if (data.status === InvoiceExtractionStatus.COMPLETED) {
                const drafts = (data.drafts || []).filter((draft: any) => {
                    const statusCode = extractStatus(draft.status)
                    return statusCode !== RecordStatus.PAID.toString()
                })

                const bulkUpdates = drafts.map((draft: any) => ({
                    recordId: draft.record_id?.toString(),
                    nights: draft.nights?.toString(),
                    rate: draft.rate?.toString(),
                    paid_ct: draft.paid_ct?.toString(),
                    status: draft.status,
                    confidence_score: draft.confidence_score,
                    payment_method: draft.payment_method,
                }))

                onDrafts(bulkUpdates)
                setIsPolling(false)
                toast.info(`Updated ${drafts.length} records.`)
                return
            }

            clearPollTimeout()
            pollTimeoutRef.current = window.setTimeout(() => {
                pollDrafts(jobIdArg)
            }, POLL_INTERVAL)
        } catch (err) {
            console.error('Draft polling error:', err)
            stopPolling(true)
            const errorMessage = err instanceof Error ? err.message : 'Error retrieving drafts.'
            toast.error(errorMessage)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!file || isUploading) return
        if (!invoiceId) {
            toast.error('Invoice not available')
            return
        }

        setIsUploading(true)
        stopPolling(true)

        try {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('invoice_id', invoiceId.toString())

            const uploadRes = await uploadInvoiceForExtraction(formData)

            const fileId = uploadRes.data?.file_id ?? uploadRes.data?.id
            if (!fileId) throw new Error('File ID not returned from upload')

            const extractRes = await runInvoiceExtraction(fileId, invoiceId)

            const extractData = extractRes.data
            const newJobId = extractData?.job_id ?? extractData?.id
            if (!newJobId) throw new Error('Extraction job ID not returned')

            setJobId(newJobId)
            setStatus(extractData?.status ?? InvoiceExtractionStatus.PENDING)
            setFile(null)
            setFileKey(Date.now())
            hasProcessedRef.current = false
            setIsPolling(true)
            pollStatus(newJobId)
            toast.success('Extraction started successfully')
        } catch (err: any) {
            console.error('Extraction submit error:', err)
            const message = err instanceof Error ? err.message : 'Failed to process invoice'
            toast.error(message)
            stopPolling(true)
        } finally {
            setIsUploading(false)
        }
    }

    const checkActiveJob = async () => {
        if (!ability.can('View', 'InvoiceExtractionJob')) {
            return
        }
        if (!invoiceId) return
        try {
            const response = await getActiveInvoiceExtractionJob(invoiceId.toString())
            const data = response.data
            if (!data || !data.job_id) {
                return
            }
            if (data.status === InvoiceExtractionStatus.FAILED) {
                stopPolling(true)
                toast.error(data.error || data.message || 'Extraction failed.')
                return
            }

            setJobId(data.job_id)
            setStatus(data.status)
            hasProcessedRef.current = false
            setIsPolling(true)
            pollStatus(data.job_id)
            toast.info('Resuming extraction job...')
        } catch (error: any) {
            if (error.response?.status === 404) {
                return
            }
            console.error('Failed to check active job:', error)
        }
    }

    // prevent unload while polling
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isPolling) return
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [isPolling])

    return {
        file,
        setFile,
        fileKey,
        isUploading,
        isPolling,
        jobId,
        status,
        handleSubmit,
        checkActiveJob,
    }
}
